<template>
  <div v-if="enabled" class="wa-group-search">
    <div class="wa-group-search__input-wrap">
      <fluent-icon icon="search" size="14" class="wa-group-search__icon" />
      <input
        v-model="query"
        type="text"
        placeholder="Buscar grupo do WhatsApp…"
        class="wa-group-search__input"
        @focus="open = true"
        @input="onInput"
        @keydown.esc="close"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="pick(selectedIndex)"
      />
      <button
        v-if="query"
        type="button"
        class="wa-group-search__clear"
        @click="clear"
      >×</button>
    </div>

    <div v-if="open" class="wa-group-search__dropdown">
      <div v-if="loading" class="wa-group-search__state">Carregando…</div>
      <div v-else-if="error" class="wa-group-search__state wa-group-search__state--err">
        {{ error }}
      </div>
      <div v-else-if="!groups.length" class="wa-group-search__state">
        Nenhum grupo encontrado
      </div>
      <button
        v-for="(g, i) in groups"
        :key="g.groupJid"
        type="button"
        class="wa-group-search__item"
        :class="{ 'wa-group-search__item--active': i === selectedIndex }"
        @mousedown.prevent="pick(i)"
        @mouseenter="selectedIndex = i"
      >
        <span class="wa-group-search__avatar">{{ initials(g.name) }}</span>
        <span class="wa-group-search__info">
          <span class="wa-group-search__name">{{ g.name }}</span>
          <span class="wa-group-search__jid">{{ g.groupJid }}</span>
        </span>
        <span v-if="g.unread > 0" class="wa-group-search__unread">
          {{ g.unread }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const BASE = window.MENTION_BRIDGE_URL || '/mention-bridge';

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export default {
  name: 'WaGroupSearch',

  props: {
    inboxId: { type: [Number, String], default: 0 },
  },

  data() {
    return {
      query: '',
      groups: [],
      open: false,
      loading: false,
      error: '',
      selectedIndex: 0,
      debouncedFetch: null,
    };
  },

  computed: {
    ...mapGetters({
      getInbox: 'inboxes/getInbox',
      currentUser: 'getCurrentUser',
    }),
    inbox() {
      return this.inboxId ? this.getInbox(this.inboxId) : null;
    },
    instanceName() {
      const url = this.inbox?.webhook_url || '';
      return url.split('/').filter(Boolean).pop() || '';
    },
    accountId() {
      return this.currentUser?.account_id
        || this.$store?.getters?.getCurrentAccountId
        || this.$route?.params?.accountId
        || null;
    },
    enabled() {
      if (!this.inbox || !this.inboxId) return false;
      const ch = this.inbox.channel_type || '';
      const isApi = ch === 'Channel::Api' || ch.endsWith('Api');
      return isApi && !!this.instanceName;
    },
  },

  watch: {
    inboxId() {
      this.reset();
    },
    open(val) {
      if (val && this.enabled && this.groups.length === 0) {
        this.fetchGroups();
      }
    },
  },

  created() {
    this.debouncedFetch = debounce(() => this.fetchGroups(), 250);
    this._outsideClick = e => {
      if (!this.$el?.contains || !this.$el.contains(e.target)) this.open = false;
    };
  },

  mounted() {
    document.addEventListener('mousedown', this._outsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this._outsideClick);
  },

  methods: {
    reset() {
      this.query = '';
      this.groups = [];
      this.open = false;
      this.selectedIndex = 0;
      this.error = '';
    },
    close() { this.open = false; },
    clear() { this.query = ''; this.onInput(); },
    onInput() {
      this.open = true;
      this.debouncedFetch();
    },
    move(delta) {
      const n = this.groups.length;
      if (!n) return;
      this.selectedIndex = ((this.selectedIndex + delta) % n + n) % n;
    },
    initials(name) {
      if (!name) return '?';
      return name.split(/\s+/).slice(0, 2).map(w => (w[0] || '').toUpperCase()).join('');
    },

    async fetchGroups() {
      if (!this.enabled) return;
      this.loading = true;
      this.error = '';
      try {
        const params = new URLSearchParams({
          instanceName: this.instanceName,
          q: this.query,
          limit: '30',
        });
        const resp = await fetch(`${BASE}/api/groups?${params}`, {
          credentials: 'same-origin',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        this.groups = data.groups || [];
        this.selectedIndex = 0;
      } catch (e) {
        this.error = `Erro ao buscar grupos: ${e.message}`;
      } finally {
        this.loading = false;
      }
    },

    async pick(index) {
      const g = this.groups[index];
      if (!g || !this.accountId) return;
      this.loading = true;
      this.error = '';
      try {
        const resp = await fetch(`${BASE}/api/groups/open-conversation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            inboxId:      this.inboxId,
            instanceName: this.instanceName,
            groupJid:     g.groupJid,
            accountId:    this.accountId,
          }),
        });
        if (!resp.ok) {
          const msg = await resp.text();
          throw new Error(msg || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        this.open = false;
        this.query = '';
        // Navigate to the conversation
        this.$router.push({
          name: 'inbox_conversation',
          params: {
            accountId:       this.accountId,
            inbox_id:        this.inboxId,
            conversation_id: data.conversationId,
          },
        }).catch(() => {
          // Fallback: try generic conversation route
          this.$router.push({
            name: 'conversation_through_inbox',
            params: {
              accountId:       this.accountId,
              inboxId:         this.inboxId,
              conversationId:  data.conversationId,
            },
          }).catch(() => {});
        });
      } catch (e) {
        this.error = `Erro ao abrir conversa: ${e.message}`;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wa-group-search {
  position: relative;
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--color-border, #e5e7eb);

  &__input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__icon {
    position: absolute;
    left: 8px;
    color: var(--color-body-light, #9ca3af);
    pointer-events: none;
  }

  &__input {
    width: 100%;
    padding: 6px 24px 6px 28px;
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 6px;
    background: var(--color-background, #fff);
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: var(--color-primary, #1f93ff);
    }
  }

  &__clear {
    position: absolute;
    right: 6px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-body-light, #9ca3af);
    font-size: 16px;
    line-height: 1;
    padding: 2px 6px;

    &:hover { color: var(--color-body, #111827); }
  }

  &__dropdown {
    position: absolute;
    left: 12px;
    right: 12px;
    top: calc(100% - 2px);
    z-index: 50;
    background: var(--color-background, #fff);
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 6px;
    box-shadow: 0 6px 20px rgba(0,0,0,.12);
    max-height: 320px;
    overflow-y: auto;
    padding: 4px 0;
  }

  &__state {
    padding: 10px 14px;
    font-size: 12px;
    color: var(--color-body-light, #6b7280);

    &--err { color: #dc2626; }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 12px;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;

    &:hover, &--active {
      background: var(--color-background-light, #f3f4f6);
    }
  }

  &__avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #10b981;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: var(--color-body, #111827);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__jid {
    font-size: 10px;
    color: var(--color-body-light, #9ca3af);
  }

  &__unread {
    flex-shrink: 0;
    background: #10b981;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 10px;
  }
}
</style>
